/**
 * W3 Panel wrapper (PMA-BCAST-013). Optional per-widget chrome: background
 * (none | colour + opacity | library image), radius, border and text shadow.
 * `panel` unset renders the children untouched, so layouts saved before W3
 * keep their exact look.
 */
import type { CSSProperties, ReactNode } from "react";

export interface PanelConfig {
  background: "none" | "color" | "image";
  color?: string;
  opacity?: number;
  asset_id?: string | null;
  radius_px?: number;
  border?: { enabled: boolean; color?: string; width_px?: number };
  text_shadow?: boolean;
}

export const TEXT_SHADOW =
  "0 0 2px rgba(0,0,0,.95), 0 0 4px rgba(0,0,0,.9), 0 1px 3px rgba(0,0,0,.9), 0 0 8px rgba(0,0,0,.6)";

export const TEXT_STROKE = "0.06em rgba(0,0,0,0.9)";
export const MUTED_OPACITY_VAR = "--bcast-muted-opacity";
export const MUTED_COLOR_VAR = "--bcast-muted-color";
export const MUTED_WEIGHT_VAR = "--bcast-muted-weight";
export const MUTED_COLOR_LIFTED = "rgba(255,255,255,0.92)";

const clamp = (n: unknown, lo: number, hi: number, fallback: number) =>
  typeof n === "number" && Number.isFinite(n)
    ? Math.min(hi, Math.max(lo, n))
    : fallback;

const hexToRgba = (hex: string | undefined, alpha: number): string => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex ?? "");
  const v = m ? m[1] : "000000";
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Pure style computation, shared by the wrapper and its tests. */
export function panelStyle(
  panel: PanelConfig | null | undefined,
  assets: Record<string, string> = {},
): CSSProperties | null {
  if (!panel) return null;
  const style: CSSProperties = {};
  if (panel.background === "color") {
    style.backgroundColor = hexToRgba(
      panel.color,
      clamp(panel.opacity, 0, 1, 1),
    );
  } else if (panel.background === "image") {
    const url = panel.asset_id ? assets[panel.asset_id] : undefined;
    if (url) {
      style.backgroundImage = `url("${url}")`;
      style.backgroundSize = "cover";
      style.backgroundPosition = "center";
      style.backgroundRepeat = "no-repeat";
    }
  }
  const radius = clamp(panel.radius_px, 0, 64, 0);
  if (radius > 0) {
    style.borderRadius = `${radius}px`;
    style.overflow = "hidden";
  }
  if (panel.border?.enabled) {
    const w = clamp(panel.border.width_px, 0, 8, 1);
    style.border = `${w}px solid ${panel.border.color || "#ffffff"}`;
  }
  if (panel.text_shadow) {
    style.textShadow = TEXT_SHADOW;
    // Outline so thin strokes survive a busy backdrop; fill painted on top.
    style.WebkitTextStroke = TEXT_STROKE;
    style.paintOrder = "stroke fill";
    // Lift muted text: widgets read these vars (with their legacy value as
    // the fallback), so panels without text_shadow keep their current look.
    (style as Record<string, string | number>)[MUTED_OPACITY_VAR] = 1;
    (style as Record<string, string | number>)[MUTED_COLOR_VAR] =
      MUTED_COLOR_LIFTED;
    (style as Record<string, string | number>)[MUTED_WEIGHT_VAR] = 600;
  }
  return style;
}

export function Panel({
  panel,
  assets,
  children,
}: {
  panel?: PanelConfig | null;
  assets?: Record<string, string>;
  children: ReactNode;
}) {
  const style = panelStyle(panel, assets);
  if (!style) return <>{children}</>;
  return (
    <div
      data-testid="broadcast-panel"
      data-panel-background={panel!.background}
      data-panel-lifted={panel!.text_shadow ? "true" : undefined}
      style={{
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        ...style,
      }}
    >
      {children}
    </div>
  );
}
